import { makeAutoObservable } from "mobx";
import { logger } from "library/core/utility";
import LocalStorage from "library/core/utility/local-storage";
import camsAudio from "library/core/utility/audio";
import { AnimatedEmoji } from "./types";
import { EMOJI_SFX_MAP, isEmojiGift } from "./emoji-sfx-config";
import { layoutStore } from "library/core/stores/layout/LayoutStore";

const logPrefix = "[EmojiAnimationStore]:";
const SOUND_EFFECTS_KEY = "emoji_sound_effects_enabled";
const DEFAULT_SOUND_EFFECTS_ENABLED = false;

const rand = (min: number, max: number) =>
  Math.random() * (max - min) + min;

class EmojiAnimationStore {
  public animatedEmojis: AnimatedEmoji[] = [];
  public floatingEffectsEnabled: boolean = true;
  public soundEffectsEnabled: boolean = DEFAULT_SOUND_EFFECTS_ENABLED;
  private cleanupTimers: number[] = [];
  private sfxPreloaded = false;

  constructor() {
    makeAutoObservable(this);
    this.loadSoundEffectsSetting();
  }

  log = (...params: any[]) => {
    logger.log(logPrefix, ...params);
  };

  toggleFloatingEffects = () => {
    this.floatingEffectsEnabled = !this.floatingEffectsEnabled;
    if (!this.floatingEffectsEnabled) {
      this.clearAnimations();
    }
  };

  toggleSoundEffects = () => {
    this.soundEffectsEnabled = !this.soundEffectsEnabled;
    LocalStorage.set(SOUND_EFFECTS_KEY, this.soundEffectsEnabled);
  };

  private loadSoundEffectsSetting = () => {
    const stored = LocalStorage.get(SOUND_EFFECTS_KEY);
    if (typeof stored === "boolean") {
      this.soundEffectsEnabled = stored;
    }
  };

  clearAnimations = () => {
    this.animatedEmojis = [];
    this.cleanupTimers.forEach(t => clearTimeout(t));
    this.cleanupTimers = [];
  };

  triggerAnimation = (emojiUrl: string, giftName: string) => {
    if (isEmojiGift(giftName)) {
      if (this.soundEffectsEnabled) {
        this.playSfx(giftName);
      }
    } else {
      camsAudio.playGiftReceived();
    }

    if (!this.floatingEffectsEnabled) return;

    this.clearAnimations();

    const isMobile = layoutStore?.isMobileScreenSize;
    const totalEmojis = isMobile
      ? Math.floor(rand(5, 9))
      : Math.floor(rand(10, 17));
    const waves = isMobile ? 2 : 3;
    const perWave = Math.ceil(totalEmojis / waves);

    for (let wave = 0; wave < waves; wave++) {
      const delay = wave * 350;
      const timer = window.setTimeout(() => {
        const start = wave * perWave;
        const end = Math.min(start + perWave, totalEmojis);
        const newEmojis: AnimatedEmoji[] = [];

        for (let i = start; i < end; i++) {
          newEmojis.push({
            id: Date.now() + Math.random() + i,
            emojiData: emojiUrl,
            left: rand(2, 92),
            drift: rand(-60, 60),
            topDistance: isMobile ? rand(300, 500) : rand(500, 800),
            duration: rand(2.5, 4.0),
            scale: isMobile ? rand(0.3, 1.0) : rand(0.5, 1.5),
            bottom: rand(0, 15),
            zIndex: Math.floor(rand(10000, 10011)),
            rotation: rand(-20, 20),
          });
        }

        this.animatedEmojis = [...this.animatedEmojis, ...newEmojis];

        newEmojis.forEach(emoji => {
          const removeTimer = window.setTimeout(() => {
            this.animatedEmojis = this.animatedEmojis.filter(
              e => e.id !== emoji.id
            );
          }, emoji.duration * 1000);
          this.cleanupTimers.push(removeTimer);
        });
      }, delay);
      this.cleanupTimers.push(timer);
    }

    this.log("triggerAnimation", giftName);
  };

  private preloadSfx = () => {
    if (this.sfxPreloaded) return;
    this.sfxPreloaded = true;
    camsAudio.preloadUrls(Object.values(EMOJI_SFX_MAP));
  };

  private playSfx = (giftName: string) => {
    this.preloadSfx();
    const url = EMOJI_SFX_MAP[giftName];
    if (url) {
      camsAudio.play(url);
    } else {
      camsAudio.playGiftReceived();
    }
  };
}

export const emojiAnimationStore = new EmojiAnimationStore();
export default EmojiAnimationStore;
